import { Plus } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { LinkedRecordBar } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  QueueStatusFilter,
  useQueueStatus,
} from '@/components/core/queue-status-filter/queue-status-filter';
import {
  TIME_OFF_QUEUE_LABEL,
  TIME_OFF_QUEUES,
  timeOffQueueFilter,
} from '../../../shared/request-queues';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import { useAuthStore } from '@/state/store/auth';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createToilRequestsTableColumns,
  ToilRequestAction,
} from '../toil-requests-table/toil-requests-table-columns';
import { ToilForEmployeeDialog } from '../toil-for-employee-dialog/toil-for-employee-dialog';
import { DEFAULT_MANAGE_TOIL_REQUESTS_ORDER } from '../../graphql/queries';
import {
  useArchiveToilRequest,
  useDeleteToilRequest,
  useGetToilRequests,
  useReviewToilRequest,
  useToilQueueCounts,
} from '../../hooks/use-toil-manager';
import { ToilRequest, ToilRequestsPagingResult } from '../../types/toil-manager.types';

/** The confirmation of each menu action: title, question and confirm button. */
const CONFIRMATION: Record<ToilRequestAction, { title: string; description: string }> = {
  Approved: { title: 'APPROVE_TOIL_REQUEST', description: 'APPROVE_TOIL_CONFIRMATION' },
  Rejected: { title: 'REJECT_TOIL_REQUEST', description: 'REJECT_TOIL_CONFIRMATION' },
  Archive: { title: 'ARCHIVE_TOIL_REQUEST', description: 'TOIL_REQUEST_ARCHIVE_WARNING' },
  Delete: { title: 'DELETE_TOIL_REQUEST', description: 'TOIL_REQUEST_DELETE_WARNING' },
};

const CONFIRM_TEXT: Record<ToilRequestAction, string> = {
  Approved: 'APPROVE',
  Rejected: 'REJECT',
  Archive: 'ARCHIVE',
  Delete: 'DELETE',
};

const RequestsTab = ({
  queueFilter,
  toolbar,
}: {
  /** The open queue's `where` (status / archived). */
  queueFilter: Record<string, unknown>;
  /** The table toolbar: the queue switch and Add TOIL for employee. */
  toolbar: ReactNode;
}) => {
  const { t } = useTranslation();
  // The TOIL workflows check these too; they only decide which actions the row menu offers.
  const canReview = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_REVIEW);
  const canArchive = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_ARCHIVE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_DELETE);
  const currentUserId = useAuthStore((state) => state.user?.itemId ?? '');

  // A notification's link lists only its request.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MANAGE_TOIL_REQUESTS_ORDER,
    linkedId,
  });
  // The request and the action picked from its menu, awaiting confirmation.
  const [target, setTarget] = useState<{
    request: ToilRequest;
    action: ToilRequestAction;
  } | null>(null);

  const { departments, getDepartmentIdCondition } = useDepartmentScope();

  // The department scope always applies, so a reviewer limited to some departments never sees
  // requests outside them; the Department column's filter only narrows within it (mergeWhere).
  const departmentCondition = getDepartmentIdCondition('');
  const filter = { ...queueFilter, ...toilScopeFilter(departmentCondition) };
  const { data, isLoading, error } = useGetToilRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { ToilRequests?: ToilRequestsPagingResult })?.ToilRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: reviewToilRequest, isPending: isReviewing } = useReviewToilRequest();
  const { mutateAsync: archiveToilRequest, isPending: isArchiving } = useArchiveToilRequest();
  const { mutateAsync: deleteToilRequest, isPending: isDeleting } = useDeleteToilRequest();
  const isRunning = isReviewing || isArchiving || isDeleting;

  // A refusal is toasted by the mutation's global error handler; the modal closes either way.
  const handleConfirm = async () => {
    if (!target) return;
    const { request, action } = target;
    try {
      if (action === 'Delete') await deleteToilRequest(request.ItemId);
      else if (action === 'Archive') await archiveToilRequest(request.ItemId);
      else await reviewToilRequest({ itemId: request.ItemId, status: action });
    } catch {
      // Already toasted.
    } finally {
      setTarget(null);
    }
  };

  // No details dialog: every action is picked from the row's menu and only confirmed.
  const columns = createToilRequestsTableColumns({
    t,
    onAction: (request, action) => setTarget({ request, action }),
    canReview,
    canArchive,
    canDelete,
    currentUserId,
    departments,
  });
  const confirmation = target ? CONFIRMATION[target.action] : undefined;
  const isDestructive = target?.action === 'Rejected' || target?.action === 'Delete';

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
        columnsToolbar={() => toolbar}
      />

      <ConfirmationModal
        open={Boolean(target)}
        onOpenChange={(open) => !open && !isRunning && setTarget(null)}
        title={confirmation ? t(confirmation.title) : ''}
        description={confirmation ? t(confirmation.description) : ''}
        confirmText={target ? CONFIRM_TEXT[target.action] : 'CONFIRM'}
        confirmClassName={isDestructive ? 'destructive' : undefined}
        preventAutoClose
        confirmDisabled={isRunning}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

/** The TOIL requests a reviewer may see: their department scope (all departments for some). */
export const toilScopeFilter = (departmentCondition: unknown) =>
  departmentCondition ? { DepartmentId: departmentCondition } : {};

/**
 * The TOIL Requests tab of Team Time Off (the former TOIL Manager): everyone's TOIL requests in the
 * reviewer's department scope, by queue (Pending, Approved, Rejected, Archived, All, each with its
 * count; Component Map › Queue status), and Add TOIL for an employee.
 */
export const TeamToilTab = () => {
  const { t } = useTranslation();
  const canRequestForOthers = useHasPermission(HRM_PERMISSIONS.TIME_OFF.TOIL_REQUEST_FOR_OTHERS);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [queue, setQueue] = useQueueStatus(TIME_OFF_QUEUES, 'Pending', 'All');
  const { getDepartmentIdCondition } = useDepartmentScope();
  const counts = useToilQueueCounts(toilScopeFilter(getDepartmentIdCondition('')));

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <QueueStatusFilter
        value={queue}
        onChange={setQueue}
        options={TIME_OFF_QUEUES.map((value) => ({
          value,
          label: TIME_OFF_QUEUE_LABEL[value],
          count: counts[value],
        }))}
      />
      {canRequestForOthers && (
        <Button type="button" variant="outline" size="sm" onClick={() => setIsAddOpen(true)}>
          <Plus className="size-4" />
          {t('ADD_TOIL_FOR_EMPLOYEE')}
        </Button>
      )}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      {/* A fresh list (first page, its own filters) for each queue. */}
      <RequestsTab key={queue} queueFilter={timeOffQueueFilter(queue)} toolbar={toolbar} />

      {canRequestForOthers && (
        <ToilForEmployeeDialog open={isAddOpen} onOpenChange={setIsAddOpen} />
      )}
    </div>
  );
};
